import { NextPage } from 'next';

import Container from 'components/Container';
import MetaTitle from 'components/MetaTitle';
import Typography from 'components/Typography';
import Layout from 'containers/Layout';
import Main from 'containers/Main';
import styles from 'styles/pages/Terms.module.css';

const TermsAndConditions: NextPage = () => {
  return (
    <>
      <MetaTitle>Terms and Conditions of Sale</MetaTitle>
      <Layout>
        <Main paddingTop>
          <Container className={styles.container}>
            <Typography component="h1" variant="big" className={styles.title}>
              Jackson Design AB – Terms and Conditions of Sale
            </Typography>

            <Typography component="p" variant="mid" className={styles.paragraph}>
              These terms apply to every sale by Jackson Design AB, org. no. 556475-9131,
              Frihamnsgatan 50, 115 56 Stockholm, Sweden, telephone +46 705454541, email{' '}
              <a href="mailto:info@jacksons.se">info@jacksons.se</a>. Together with our order
              confirmation they form the contract between us.
            </Typography>
            <Typography component="p" variant="mid" className={styles.paragraph}>
              Swedish law applies. If you are buying as a consumer, that is, mainly for private
              purposes, mandatory Swedish consumer law applies to your purchase and nothing in these
              terms limits it. Where a clause below is marked for trade buyers, it does not apply to
              you.
            </Typography>

            <section>
              <Typography component="h2" variant="heading" className={styles.heading}>
                1. The contract
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                The contract is formed when you accept our offer in writing. These terms govern the
                sale, together with what is agreed in our order confirmation and the notes on our
                invoice. If we agree something specific to your order, that is what applies.
              </Typography>
            </section>

            <section>
              <Typography component="h2" variant="heading" className={styles.heading}>
                2. Condition
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Our pieces are vintage and are sold in the condition they are in. They were not
                manufactured to current standards and, unless we say a piece has been tested, it has
                not been. Wear, patina, marks, old repairs, shrinkage and variation in finish are
                characteristics of age, not faults.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                The materials in a vintage piece show their age. Original upholstery, padding, foam,
                webbing and springs may be worn, brittle or degraded and may not suit daily use
                until renewed. Leather may be dry, cracked or scuffed. Wood moves with humidity and
                may show old shrinkage cracks or lifted veneer. Original mirror and glass may show
                spotting, bubbles or waviness. Metal may show oxidation and pitting, and textiles
                may be faded. In a vintage piece these are characteristics of its age and materials,
                not defects, unless our description says otherwise.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Where a piece needs work, we restore it before it is offered, carefully and to the
                best condition its age allows. Even after restoration a piece remains vintage and
                should be handled with care.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Our pieces are sold as collectors&apos; items, valued for their design and
                decorative quality. Where we describe a piece as needing restoration, re-upholstery
                or rewiring, it is sold on that basis and is not ready for use until that work has
                been done.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                If you are a consumer, none of this limits your rights where a piece is not as
                described.
              </Typography>
            </section>

            <section>
              <Typography component="h2" variant="heading" className={styles.heading}>
                3. Attribution and title
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Designer, maker, model, date and provenance are given in good faith and reflect
                current research. In our descriptions:
              </Typography>
              <ul className={styles.list}>
                <li>
                  a piece listed under a designer&apos;s or maker&apos;s name alone means we have
                  researched the piece and attribute it to that designer or maker with confidence;
                </li>
                <li>
                  “attributed to” means that in our opinion the piece is probably by the named
                  designer or maker, though with less certainty than a plain listing;
                </li>
                <li>
                  “in the style of” or “in the manner of” means the piece is of the period or later
                  and recalls the named designer&apos;s work, and we do not say it is by them;
                </li>
                <li>“stamped”, “signed” or “labelled” means the piece bears that mark.</li>
              </ul>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                An attribution is based on the research available when the piece is sold. Design
                history is a living field, and what is known about a designer&apos;s work can
                develop over time.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                We are entitled to sell every piece we offer, and ownership comes to you free of any
                third-party right or claim.
              </Typography>
            </section>

            <section>
              <Typography component="h2" variant="heading" className={styles.heading}>
                4. Electrical pieces
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                All electrical and mechanical goods are sold on the basis of their artistic and
                decorative value only, and should not be assumed to be operative and/or functional.
                It is essential that prior to any intended use, the electrical system is checked and
                approved by a qualified electrician.
              </Typography>
            </section>

            <section>
              <Typography component="h2" variant="heading" className={styles.heading}>
                5. Price and tax
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Prices exclude delivery, packing, storage, insurance, customs duty and import taxes
                unless we say otherwise.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Most pieces are sold under the margin scheme for second-hand goods and antiques (in
                Sweden, vinstmarginalbeskattning): VAT is calculated on our margin, is not shown
                separately and cannot be reclaimed by you. Where we invoice free of Swedish VAT for
                export outside the EU, that treatment depends on the piece actually leaving the EU
                and on our receiving proof of export; without that proof we may invoice you the VAT.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Some sales attract a resale royalty (följerätt) under the Swedish Copyright Act.
                Where it applies we tell you the exact amount before you commit and show it on the
                invoice.
              </Typography>
            </section>

            <section>
              <Typography component="h2" variant="heading" className={styles.heading}>
                6. Payment and ownership
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Payment is due within ten working days of the invoice date, in the invoice currency.
                The same applies to a pro forma invoice: payment is due within ten working days of
                its date, and where we have issued a pro forma invoice the final invoice follows
                once payment has cleared. All bank and conversion charges are at your expense. Late
                payment carries interest under the Swedish Interest Act (räntelagen 1975:635).
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Ownership passes only when we have received the full price in cleared funds. Until
                then you may not sell, pledge or part with the piece, and you must keep it insured
                and identifiable. If we are not paid, we may cancel the sale and offer the piece
                elsewhere.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Our bank details never change by email. If you receive a message saying they have,
                do not act on it: telephone us on +46 705454541, using that number rather than one
                taken from the message, and we will then confirm the correct details in writing from{' '}
                <a href="mailto:info@jacksons.se">info@jacksons.se</a>. Account details are
                confirmed only when you have received them from us in writing after that call.
              </Typography>
            </section>

            <section>
              <Typography component="h2" variant="heading" className={styles.heading}>
                7. Delivery and risk
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Delivery is not included in the price unless stated. For a consumer order, our offer
                and order confirmation will state whether we offer to assist with transport or
                whether the piece is collection only.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Where we offer to arrange packing, freight, insurance or customs formalities, we do
                so on your behalf. We are not a carrier, and carriers&apos; and forwarders&apos; own
                terms and liability limits apply to what they do, but they do not limit our
                obligations to a consumer. Where the shipping price includes transit insurance, the
                policy governs what it pays; we handle a claim where the risk is ours and support
                yours where it is not. Keep the packing and photograph the piece on arrival, which
                makes any claim far easier. If a piece arrives damaged, tell us within 48 hours of
                delivery, with photographs of the piece and the packing. For a consumer, this
                request does not limit your rights under clause 10.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                For some pieces, the piece/pieces is collection only. If you are buying privately as
                a consumer, you must then collect the piece, or have a carrier appointed by you
                collect it, from our warehouse at the collection address stated in the order
                confirmation. We do not book or arrange transport for a collection-only sale. At
                your request, we may provide contact details for independent carriers, but you
                choose, instruct, contract with and pay the carrier directly. Any packing or crating
                we agree to provide is separate from the transport.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                If you are a consumer, the piece remains at our risk until you or a person
                designated by you receives it. If you independently appoint a carrier that we did
                not offer, the risk passes when the piece is handed to that carrier. If you collect
                the piece yourself, the risk passes on collection. Providing carrier contact details
                does not alter any mandatory rule about risk.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                For trade buyers, pieces are sold Ex Works, or FCA where we arrange export, at
                Frihamnsgatan 50, 115 56 Stockholm (Incoterms 2020), and risk passes at the point
                that rule fixes.
              </Typography>
            </section>

            <section>
              <Typography component="h2" variant="heading" className={styles.heading}>
                8. Collection and storage
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                We store a purchased piece free of charge for 14 days. If a piece is not collected
                we will write to you. For a consumer, any sale of an uncollected piece follows the
                Swedish Act on a trader&apos;s right to sell uncollected goods (lag 1985:982), with
                notice and a proper accounting to you. For trade buyers, we may resell a piece
                uncollected three months after we said it was ready and apply the proceeds against
                what you owe.
              </Typography>
            </section>

            <section>
              <Typography component="h2" variant="heading" className={styles.heading}>
                9. Export, customs and permits
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                If a piece is going abroad, you are the importer. Customs duty, import VAT,
                brokerage, entry and handling fees, demurrage and storage at a customs or bonded
                warehouse at the destination are yours. This includes charges billed to us because
                our name is on the export paperwork, and you will reimburse us for them on demand.
                This obligation continues after the piece has been delivered.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                You must clear the piece and take it out of the customs or bonded warehouse within
                the free period allowed, give us correct consignee details and any customs
                registration number needed before despatch, and tell us at once of any delay,
                customs hold or failed delivery. If you do not respond, you authorise us to give
                instructions to the carrier, warehouse or customs authority on your behalf,
                including to return or re-export the piece. If a consignment is abandoned, we may
                return, re-export, store or sell it at your cost, and you will pursue, or assign to
                us, any claim against a carrier or warehouse at fault.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Some pieces need a CITES certificate, or an export permit from the Swedish National
                Heritage Board (Riksantikvarieämbetet) or the EU. We check this before a piece is
                offered, and a piece needing an Annex A CITES certificate is not offered for sale
                until we hold one. Where a permit is needed for despatch we apply for it and
                despatch depends on it being granted; if it is refused, either of us may cancel and
                we refund the price.
              </Typography>
            </section>

            <section>
              <Typography component="h2" variant="heading" className={styles.heading}>
                10. If something is wrong
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Tell us as soon as you can, with photographs, and keep the packing.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                If you are a consumer, you have the right to complain about faults under mandatory
                consumer law. Complaints may be sent to{' '}
                <a href="mailto:info@jacksons.se">info@jacksons.se</a> or Jackson Design AB,
                Frihamnsgatan 50, 115 56 Stockholm.
              </Typography>
            </section>

            <section>
              <Typography component="h2" variant="heading" className={styles.heading}>
                11. Changing your mind
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                If you are a consumer habitually resident in the European Economic Area and the
                purchase was concluded at a distance, by our website, email or telephone, you may
                cancel within 14 days of receiving the piece. We give you the instructions and the
                standard cancellation form before you commit. For a consumer resident outside the
                EEA these terms give no right to cancel.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                If you are purchasing privately at a distance, you may withdraw from the purchase
                within 14 days of receiving the piece. Notify Jackson Design AB clearly at{' '}
                <a href="mailto:info@jacksons.se">info@jacksons.se</a> or Frihamnsgatan 50, 115 56
                Stockholm. You must send the piece back within 14 days after notifying us. You must
                arrange and pay the return transport, estimated at approximately the delivery charge
                we quote for this order. The original packing should be used and it should be packed
                in the same manner as it was originally packed. The piece and packing should be
                photographed before collection. We will refund your payments, including standard
                outbound delivery, within 14 days of your notice, using the same payment method. We
                may wait until the piece has been returned and the condition has been checked. We
                may deduct the actual loss in value caused by handling beyond what was necessary to
                examine the piece.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Withdrawal form: To Jackson Design AB, Frihamnsgatan 50, 115 56 Stockholm,{' '}
                <a href="mailto:info@jacksons.se">info@jacksons.se</a>. I/We hereby give notice that
                I/We withdraw from the contract for the following goods: [object/order reference].
                Ordered on/received on: [date]. Name: [name]. Address: [address]. SixtyDate: [date].
                Signature: [only if submitted on paper].
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                There is no right to cancel where the purchase was concluded in person at our
                gallery or at our stand at a fair, or for a piece made or restored to your own
                specification.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Trade buyers have no right of return.
              </Typography>
            </section>

            <section>
              <Typography component="h2" variant="heading" className={styles.heading}>
                12. Identity, money laundering and sanctions
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Some of our transactions require us to identify our client and, in some cases, to
                establish the source of funds and the beneficial owner, and to screen against
                sanctions lists. A sale is conditional on those checks, which we complete before we
                are committed and before we release the piece. Payment must come from an account in
                your own name; we may approve another payer in advance and in writing, and we do not
                accept cash. Where the law requires it we may decline or cancel a sale, and we
                cannot always tell you why.
              </Typography>
            </section>

            <section>
              <Typography component="h2" variant="heading" className={styles.heading}>
                13. Data, law and disputes
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                We handle personal data in accordance with our privacy notice, which is publish at{' '}
                <a href="https://www.jacksons.se/privacy">www.jacksons.se/privacy</a>.
              </Typography>
              <Typography component="p" variant="mid" className={styles.paragraph}>
                Swedish law governs these terms. If you are a consumer you keep the protection of
                the mandatory law of the country where you live, and you may refer a dispute to the
                Swedish National Board for Consumer Disputes, Allmänna reklamationsnämnden (ARN),
                Box 174, 101 23 Stockholm, <a href="https://www.arn.se">www.arn.se</a>, which
                charges a fee of SEK 150 and issues recommendations. For trade buyers, the Swedish
                courts have exclusive jurisdiction and Stockholm District Court (Stockholms
                tingsrätt) is the court of first instance; the UN Convention on Contracts for the
                International Sale of Goods does not apply.
              </Typography>
            </section>

            <Typography component="p" variant="mid" className={styles.paragraph}>
              Questions: <a href="mailto:info@jacksons.se">info@jacksons.se</a>, +46 705454541.
            </Typography>
          </Container>
        </Main>
      </Layout>
    </>
  );
};

export default TermsAndConditions;
